import { useQuery } from "@tanstack/react-query";
import { GetPinnedNotes } from "Services/API/VisitPdf.service";
import { Spinner } from "Components/Common/LoadingSpinner/Spinner";
import "Colors.css";

/**
 * Pinned notes for a patient, read-only.
 *
 * These are the providers' internal chart reminders ("difficult stick, use the left
 * arm"). They are NOT part of the medical record and are never shown to the patient,
 * so this panel is view-only: creating, editing and archiving stay in the provider
 * app, where the clinician who owns the chart is the one making the change. The API
 * enforces that too — admin tokens are refused on write.
 */

/** e.g. "Aug 8, 2026, 3:00 PM PT" — clinic time, with the zone spelled out. */
const formatStamp = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return (
    d.toLocaleString("en-US", {
      timeZone: "America/Los_Angeles",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }) + " PT"
  );
};

export const PinnedNotesPanel = ({ patientId }) => {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["adminPinnedNotes", patientId],
    queryFn: () => GetPinnedNotes(patientId, true),
    enabled: !!patientId,
  });

  const entries = data || [];
  const active = entries.filter((e) => !e.isArchived);

  if (!patientId) return null;

  return (
    <div className="w-full mb-4 rounded-lg border border-gray-200 bg-white">
      <div className="flex items-baseline gap-2 px-4 pt-3">
        <h3 className="text-sm font-semibold sapphire">Pinned Notes</h3>
        <span className="text-xs bali_hai">
          Internal context only - not part of the medical record
        </span>
        <span className="ml-auto text-xs bali_hai">
          {active.length} active{entries.length !== active.length
            ? ` · ${entries.length - active.length} archived`
            : ""}
        </span>
      </div>

      <div className="px-4 pb-3 pt-2">
        {isLoading && <Spinner />}

        {isError && (
          <p className="text-xs text-red-500">
            {error?.message || "Could not load pinned notes."}
          </p>
        )}

        {!isLoading && !isError && entries.length === 0 && (
          <p className="text-sm text-gray-400">Nothing pinned for this patient.</p>
        )}

        <div className="flex flex-col gap-2">
          {entries.map((entry, index) => (
            <div
              key={entry._id || entry.id || index}
              className={`flex items-start gap-3 ${entry.isArchived ? "opacity-60" : ""}`}
            >
              <span className="mt-1 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-blue-chill text-[10px] font-semibold text-white">
                {index + 1}
              </span>
              <div className="min-w-0 flex-1 rounded-md border border-gray-200 bg-gray-50 p-3">
                {entry.isArchived && (
                  <div className="mb-1 text-[10px] font-semibold tracking-wide bali_hai">
                    ARCHIVED
                  </div>
                )}
                <p className="whitespace-pre-wrap text-sm sapphire">{entry.body}</p>
                <div className="mt-2 flex flex-wrap gap-x-3 text-xs bali_hai">
                  <span className="font-medium sapphire">{entry.authorName || "Staff"}</span>
                  <span>Pinned {formatStamp(entry.createdAt)}</span>
                  {entry.lastEditedAt ? (
                    <span>
                      Edited by {entry.lastEditedByName || entry.authorName || "Staff"} ·{" "}
                      {formatStamp(entry.lastEditedAt)}
                    </span>
                  ) : entry.editedByName ? (
                    <span>
                      Edited by {entry.editedByName} · {formatStamp(entry.updatedAt)}
                    </span>
                  ) : null}
                  {entry.isArchived && (
                    <span>
                      Archived{entry.archivedByName ? ` by ${entry.archivedByName}` : ""}
                      {entry.archivedAt ? ` · ${formatStamp(entry.archivedAt)}` : ""}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PinnedNotesPanel;
